import { useMemo } from "react";
import { bool, func, node, number, oneOfType, string } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateTableSettings,
  DragHandleCell,
  PinnableCell,
  ExpandContainer,
} from "@tools";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import { LabelColorPicker } from "@components/LabelEditor/components/LabelColorPicker";

export const TimelineSectionInner = observer(({
  name,
  color,
  fontColor,
  onColorChange,
  onFontColorChange,
  actions,
  tableDepth,
  expandable,
  expanded,
  onExpand,
  children,
}) => {
  const { t } = useTranslation();
  const { allowEdition, isPdfPrinterView } = useEstimateEditorSettings();
  const editorStore = useEstimateEditorStore();
  const { fullSectionActions } = useEstimateTableSettings();
  const classes = useStyle();

  const size = useMemo(() => (
    tableDepth === 2 ? "2XLarge" : "medium"
  ), [ tableDepth ]);

  return (
    <Grid
      item container
      wrap="nowrap"
      className={classnames(
        "hoverRow bg-secondary preset-page",
        expandable && "cursor-pointer",
        classes.root,
      )}
      onClick={onExpand}
    >
      {
        !isPdfPrinterView && <DragHandleCell size={size} displayHandle={allowEdition} />
      }
      <PinnableCell
        boldText
        hasStaticWidth
        allowOverflowDisplay
        tableDepth={tableDepth}
        size={size}
        actions={actions}
        wider={fullSectionActions}
        containers={ <ExpandContainer
          size={size}
          tableDepth={tableDepth}
          expandable={expandable}
          onExpand={onExpand}
        /> }
      >
        <Grid
          item
          container
          alignItems="center"
          wrap="nowrap"
          className={classnames(
            "name",
            "preset-timelineTitleText",
            tableDepth === 2 && "font-bolder",
            tableDepth < 4 && "text-md",
            tableDepth === 4 && "text-sm text-secondary",
            !name && "semi-transparent",
          )}
        >
          <span className="mr-1">
            { name || t("common.unnamed" )}
          </span>
          {
            !isPdfPrinterView &&
            <LabelColorPicker
              buttonType="dot"
              className="m-1"
              hoverIcon={!expanded}
              backgroundColor={color}
              fontColor={fontColor}
              disabled={!allowEdition}
              onColorChange={onColorChange || (() => {})}
              onFontColorChange={onFontColorChange || (() => {})}
              onAdditionalColorChange={onColorChange || (() => {})}
              onAdditionalFontColorChange={onFontColorChange || (() => {})}
              additionalColors={editorStore.additionalColors}
              additionalFontColors={editorStore.additionalFontColors}
              onAdditionalColorConfirm={editorStore.addAdditionalColor}
              onAdditionalFontColorConfirm={editorStore.addAdditionalFontColor}
              tooltipText={t("views.editor.timeline_table.color_tooltip")}
            />
          }
          { children }
        </Grid>
      </PinnableCell>
    </Grid>
  );
});

TimelineSectionInner.propTypes = {
  name: oneOfType([string, node]).isRequired,
  color: string.isRequired,
  fontColor: string,
  onColorChange: func,
  onFontColorChange: func,
  actions: node.isRequired,
  tableDepth: number.isRequired,
  expandable: bool,
  expanded: bool,
  onExpand: func,
  children: node,
  chart: node,
};

const useStyle = makeStyles(() => ({
  root: {
    width: "100%",
    minWidth: 0,
  },
}));
